// Where the writer left off in each project, in this browser (the Mac's UserDefaults `leftOff-<project>`, here
// localStorage `ws:leftOff-<project>`): the fragment and the caret in Unicode scalars, as the Mac keeps it, and when.
// Written by Write as the caret moves; read by the Library's Home, Write with nothing open, and Write itself (to put
// the caret back). Opening isn't an edit, so it never syncs.
import { scalarOffsetOfUTF16, utf16OffsetOfScalar, type Project } from '@writing-suite/core';
import { readLeftOff, validLeftOff, type LeftOff } from '../../../core/src/home/index.ts';

const KEY = (projectId: string) => `ws:leftOff-${projectId}`;

/** The stored LeftOff, if it still points at a document here (a record from before W7 is converted to scalars). */
export function loadLeftOff(projectId: string, project: Project | null, textOf?: (fragmentId: string) => string | null): LeftOff | null {
  let raw: unknown = null;
  try {
    raw = JSON.parse(localStorage.getItem(KEY(projectId)) ?? 'null');
  } catch {
    return null;
  }
  const read = readLeftOff(raw);
  if (!read) return null;
  if (project && !validLeftOff(project, read)) return null;
  const { utf16, ...leftOff } = read;
  if (!utf16) return leftOff;
  const text = textOf?.(read.fragmentId) ?? project?.fragment(read.fragmentId)?.text ?? null;
  return text === null ? leftOff : { ...leftOff, offset: scalarOffsetOfUTF16(text, read.offset) };
}

/** Records the caret (UTF-16 in the fragment's text, as the editor reports it). */
export function saveLeftOff(projectId: string, fragmentId: string, utf16: number, text: string, now = Date.now()) {
  const value: LeftOff = { fragmentId, offset: scalarOffsetOfUTF16(text, utf16), at: now };
  try {
    localStorage.setItem(KEY(projectId), JSON.stringify(value));
  } catch {
    // kept for this page only: nothing to keep it in
  }
}

/** The caret as the editor places it (UTF-16). */
export function leftOffUTF16(leftOff: LeftOff, text: string): number {
  return utf16OffsetOfScalar(text, leftOff.offset);
}
